/**
 * Explicit axis tick generation — shared by every renderer.
 *
 * Phase 1 of the d3 migration (see `docs/d3-migration-plan.md` §6). Highcharts
 * chooses datetime ticks, extends axis maxima to tick boundaries, and picks label
 * formats implicitly. Reimplementing those heuristics in d3 from scratch would mean
 * discovering mismatches through pixel diffs.
 *
 * So instead: derive the ticks here, hand them to Highcharts via `tickPositions`,
 * and confirm the rendering is unchanged. That converts "match their algorithm"
 * into "both renderers consume one declared set of numbers" — and it is verifiable
 * while Highcharts is still the renderer.
 */

/* ------------------------------------------------------------------ *
 * Linear ticks — bar and horizontal-bar value axes
 * ------------------------------------------------------------------ */

export interface LinearTickOptions {
  /**
   * Axis floor while the data stays at or above it — Highcharts' `softMin`.
   * A *soft* floor on purpose: if the data dips below, the axis follows it down
   * rather than clipping the bars. Both value axes use 0.
   */
  softMin: number;
  /** Smallest value in the data. */
  dataMin: number;
  /** Largest value in the data. */
  dataMax: number;
  /** Axis max never falls below this (bar: 30, horizontal bar: 45). */
  floorMax: number;
  /** Spacing between ticks (both charts use 5). */
  interval: number;
}

/**
 * Highcharts snaps both ends of an axis out to tick boundaries (`startOnTick` /
 * `endOnTick`) rather than stopping mid-interval. Replicated here so both ends
 * become ours to declare.
 *
 * Verified against the frozen baselines: bar chart data −0.95…33.89 with softMin 0
 * yields −5…35, and horizontal bar data max 50.25 with floor 45 yields 0…55.
 */
export function linearTicks(opts: LinearTickOptions): { min: number; max: number; positions: number[] } {
  const { softMin, dataMin, dataMax, floorMax, interval } = opts;

  const max = Math.ceil(Math.max(floorMax, dataMax) / interval) * interval;
  // Below the soft floor the axis extends down to the next tick boundary; at or
  // above it the floor holds exactly.
  const min = dataMin < softMin ? Math.floor(dataMin / interval) * interval : softMin;

  const positions: number[] = [];
  const steps = Math.round((max - min) / interval);
  for (let i = 0; i <= steps; i += 1) {
    positions.push(Number((min + i * interval).toFixed(10)));
  }
  return { min, max, positions };
}

/* ------------------------------------------------------------------ *
 * Width-aware value ticks — horizontal bar
 * ------------------------------------------------------------------ */

/**
 * Minimum breathing room between value-axis ticks.
 *
 * DELIBERATE DESIGN DECISION, not a derived constant. Highcharts overrides even an
 * explicit `tickInterval` on narrow plots and re-derives both the interval and the
 * axis max; that heuristic is undocumented and we are deleting it anyway, so we
 * declare our own rule instead of imitating one we cannot fully justify.
 *
 * Measured from the frozen baselines, any value in **(14.9, 27.3]** reproduces the
 * current rendering at all three review viewports:
 *   desktop plot 1003px → interval 5 (12 ticks, 91px apart)
 *   tablet  plot  491px → interval 5 (12 ticks, 45px apart)
 *   mobile  plot  164px → interval 10 (7 ticks, 27px apart)
 * 24 sits comfortably inside that window rather than on either edge.
 *
 * Consequence, accepted knowingly: tick density at widths we have not captured is
 * now our behavior rather than Highcharts'. The embeds are fixed-width iframes
 * reviewed at these three sizes, so the exposure is small — but it is real.
 */
export const VALUE_AXIS_MIN_TICK_SPACING_PX = 24;

/** Candidate intervals, a 1-2-5 progression scaled to percentage-style axes. */
const VALUE_TICK_INTERVALS = [5, 10, 25, 50, 100] as const;

export interface ValueAxisTickOptions extends Omit<LinearTickOptions, "interval"> {
  /** Rendered width of the plot area in px. */
  plotWidth: number;
  minSpacingPx?: number;
  intervals?: readonly number[];
}

/**
 * Pick the finest interval whose resulting ticks stay at least `minSpacingPx` apart.
 * Falls back to the coarsest candidate when even that is too dense — a cramped axis
 * beats throwing on a very narrow container.
 */
export function valueAxisTicks(opts: ValueAxisTickOptions): {
  min: number;
  max: number;
  positions: number[];
  interval: number;
} {
  const {
    plotWidth,
    minSpacingPx = VALUE_AXIS_MIN_TICK_SPACING_PX,
    intervals = VALUE_TICK_INTERVALS,
    ...bounds
  } = opts;

  let last = linearTicks({ ...bounds, interval: intervals[intervals.length - 1]! });
  for (const interval of intervals) {
    const ticks = linearTicks({ ...bounds, interval });
    const gaps = ticks.positions.length - 1;
    const spacing = gaps > 0 ? plotWidth / gaps : Infinity;
    last = ticks;
    if (spacing >= minSpacingPx) return { ...ticks, interval };
  }
  return { ...last, interval: intervals[intervals.length - 1]! };
}

/* ------------------------------------------------------------------ *
 * Datetime ticks — line chart X axis
 * ------------------------------------------------------------------ */

/**
 * Month-count steps Highcharts will consider before escalating to whole years,
 * matching its default `units` table for the month unit.
 */
const MONTH_STEPS = [1, 2, 3, 4, 6] as const;
/** Year steps, mirroring Highcharts' [1, 2, 5, 10] progression. */
const YEAR_STEPS = [1, 2, 5, 10] as const;
/** Highcharts' default `tickPixelInterval` for an X axis. */
export const TARGET_TICK_PIXELS = 100;

/**
 * Minimum horizontal room a date label needs.
 *
 * Targeting ~100px per tick alone let d3 fit one more tick than Highcharts at 768px,
 * which is what read as crowding in QA. Enforcing a floor makes crowding structurally
 * impossible at any width rather than tuned per breakpoint.
 */
export const MIN_DATE_LABEL_SPACING_PX = 74;

const MONTH_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/**
 * Fraction of the data range added beyond each end of a datetime axis —
 * Highcharts' `minPadding` / `maxPadding` default for non-category axes.
 *
 * This is what put a `Jul 2026` tick on a chart whose last point is 2026-06-30:
 * the range is 107,654,400,000 ms, so 1% is ~12.46 days and the axis actually ends
 * 2026-07-13. It equally explains the *absent* Jan 2023 tick, since the axis starts
 * 2023-01-18. Reproduces the rendered tick counts at all three viewports (10 / 7 / 3).
 */
export const DATETIME_AXIS_PADDING_RATIO = 0.01;

export interface DatetimeTickOptions {
  /** Earliest data point, epoch ms (padding is applied here, not by the caller). */
  min: number;
  /** Latest data point, epoch ms. */
  max: number;
  /** Plot width in px — drives how many ticks fit. */
  plotWidth: number;
  /** Override the ~100px target if a caller needs denser or sparser ticks. */
  targetTickPixels?: number;
  /** Override the axis padding; defaults to {@link DATETIME_AXIS_PADDING_RATIO}. */
  paddingRatio?: number;
}

export interface DatetimeTicks {
  positions: number[];
  /** `month` → "May 2023"; `year` → "2023". */
  unit: "month" | "year";
  /** Months per step (12, 24, 60, 120 when `unit` is year). */
  stepMonths: number;
  /** Padded axis bounds, epoch ms — declare these on the axis alongside the ticks. */
  axisMin: number;
  axisMax: number;
}

function monthsBetween(a: Date, b: Date): number {
  return (b.getUTCFullYear() - a.getUTCFullYear()) * 12 + (b.getUTCMonth() - a.getUTCMonth());
}

/** Pick the allowed step whose resulting spacing sits closest to the pixel target. */
function chooseStepMonths(totalMonths: number, plotWidth: number, targetPx: number): {
  stepMonths: number;
  unit: "month" | "year";
} {
  const pxPerMonth = plotWidth > 0 && totalMonths > 0 ? plotWidth / totalMonths : 0;
  if (pxPerMonth <= 0) return { stepMonths: 12, unit: "year" };

  const idealMonths = targetPx / pxPerMonth;

  const candidates: Array<{ stepMonths: number; unit: "month" | "year" }> = [
    ...MONTH_STEPS.map((m) => ({ stepMonths: m, unit: "month" as const })),
    ...YEAR_STEPS.map((y) => ({ stepMonths: y * 12, unit: "year" as const })),
  ];

  let best = candidates[0]!;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const distance = Math.abs(candidate.stepMonths - idealMonths);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = candidate;
    }
  }
  return best;
}

/**
 * Ticks land on a grid anchored at January of the axis-minimum's year, and only
 * those strictly inside the axis range are kept — which is why a chart starting
 * 31 Jan 2023 with a 4-month step begins at May 2023 rather than January.
 */
export function datetimeTicks(opts: DatetimeTickOptions): DatetimeTicks {
  const {
    min,
    max,
    plotWidth,
    targetTickPixels = TARGET_TICK_PIXELS,
    paddingRatio = DATETIME_AXIS_PADDING_RATIO,
  } = opts;

  // Pad first: which ticks fall inside the axis depends on the padded bounds, not
  // the raw data extremes.
  const padding = (max - min) * paddingRatio;
  const axisMin = min - padding;
  const axisMax = max + padding;

  const totalMonths = Math.max(1, monthsBetween(new Date(axisMin), new Date(axisMax)));
  let { stepMonths, unit } = chooseStepMonths(totalMonths, plotWidth, targetTickPixels);

  // Step up until labels have room. Without this the ~100px target can still leave
  // ticks closer together than a date label can occupy.
  const candidates = [...MONTH_STEPS.map((m) => ({ stepMonths: m, unit: "month" as const })),
    ...YEAR_STEPS.map((y) => ({ stepMonths: y * 12, unit: "year" as const }))];
  for (const candidate of candidates) {
    if (candidate.stepMonths < stepMonths) continue;
    const spacing = plotWidth / Math.max(1, totalMonths / candidate.stepMonths);
    stepMonths = candidate.stepMonths;
    unit = candidate.unit;
    if (spacing >= MIN_DATE_LABEL_SPACING_PX) break;
  }

  const anchorYear = new Date(axisMin).getUTCFullYear();
  const positions: number[] = [];
  // Start a step below the floor so a tick landing exactly on the axis minimum is
  // still considered, then filter to the padded range.
  for (let offset = -stepMonths; ; offset += stepMonths) {
    const t = Date.UTC(anchorYear, offset, 1);
    if (t > axisMax) break;
    if (t >= axisMin) positions.push(t);
    if (offset > 12 * 500) break; // guard against a pathological step
  }

  return { positions, unit, stepMonths, axisMin, axisMax };
}

/** Label text for a datetime tick, matching Highcharts' `%b %Y` / `%Y` output. */
export function datetimeTickLabel(epochMs: number, unit: "month" | "year"): string {
  const d = new Date(epochMs);
  const year = d.getUTCFullYear();
  if (unit === "year") return String(year);
  return `${MONTH_ABBR[d.getUTCMonth()]} ${year}`;
}
